import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const getSessionId = () => {
  let s = localStorage.getItem('aiChatSession');
  if (!s) {
    s = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('aiChatSession', s);
  }
  return s;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Reusable Ask AI chat. Used both full-page (/ask-ai) and inside the floating widget.
 * Shares one backend (/api/ai/chat/*), persisted history, and daily budget metering.
 *
 * Props:
 *   fullPage  - render the taller full-page layout (default false = compact widget height)
 *   active    - when false, skip the initial history fetch (lets the widget defer loading
 *               until first opened). Default true.
 */
const AskAIChat = ({ fullPage = false, active = true }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState(null);
  const [notice, setNotice] = useState('');
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  const scrollToEnd = () => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  // Load persisted transcript + today's usage once, when active.
  useEffect(() => {
    if (!active || loaded) return;
    setLoaded(true);
    fetch(`${getApiUrl()}/api/ai/chat/history?limit=50`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        if (Array.isArray(d.messages)) setMessages(d.messages);
        setUsage(d);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, user]);

  useEffect(scrollToEnd, [messages, sending]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setNotice('');
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    try {
      const resp = await fetch(`${getApiUrl()}/api/ai/chat/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ question: q, sessionId: getSessionId() }),
      });
      const d = await resp.json().catch(() => ({}));
      if (d && d.allowed && d.answer) {
        setMessages((m) => [...m, { role: 'assistant', content: d.answer }]);
        setUsage(d);
      } else {
        setNotice(d && d.error ? d.error : 'AI is temporarily unavailable. Please try again.');
        if (d) setUsage(d);
      }
    } catch (e) {
      setNotice('AI is temporarily unavailable. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const remaining = usage && typeof usage.questionsRemaining === 'number' ? usage.questionsRemaining : null;
  const limit = usage && typeof usage.questionLimit === 'number' ? usage.questionLimit : null;

  return (
    <div className="flex flex-col" style={{ height: fullPage ? '60vh' : '100%' }}>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-6 text-sm">
            <p>Ask about stocks, analysis, or how to use GandalStock.</p>
            <p className="mt-1">e.g. “What’s AMZN’s support level?”</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 rounded-lg px-3 py-2 text-sm">Thinking…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {notice && (
        <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-100">
          {notice}
        </div>
      )}

      <div className="border-t border-gray-200 p-2 flex gap-2">
        <textarea
          className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={fullPage ? 2 : 1}
          maxLength={1500}
          placeholder="Ask a question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={sending}
        />
        <button
          className="btn-primary px-4 text-sm"
          onClick={send}
          disabled={sending || !input.trim()}
        >
          Send
        </button>
      </div>

      <div className="flex items-center justify-between px-3 pb-2 pt-1">
        <span className="text-[11px] text-gray-400">
          AI can make mistakes. Not financial advice.
        </span>
        {remaining !== null && limit !== null && (
          <span className="text-[11px] text-gray-400">{remaining}/{limit} left today</span>
        )}
      </div>
    </div>
  );
};

export default AskAIChat;
